import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Crown, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { buildWhatsAppLink } from "@/components/FloatingWhatsAppButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROGRAMS = [
  {
    id: "monthly",
    name: "Monthly Coaching",
    price: "$500",
    unit: "USD / month",
    tag: null,
    tagline: "For clients who want ongoing personalized coaching and accountability.",
    cta: "Get Started",
    perks: [
      "Personalized nutrition plan",
      "Custom training program",
      "Monthly check-ins & adjustments",
      "Direct access to coaching team",
    ],
  },
  {
    id: "six_month",
    name: "6 Month Transformation",
    price: "$2,000",
    unit: "USD total",
    tag: null,
    tagline: "A structured six-month transformation designed for serious long-term progress.",
    cta: "Start My Transformation",
    perks: [
      "6-month structured roadmap",
      "Bi-weekly progress reviews",
      "Nutrition + training + cardio plan",
      "Priority team support",
    ],
  },
  {
    id: "twelve_month",
    name: "12 Month Elite",
    price: "$3,500",
    unit: "USD — Promotional Price",
    tag: "BEST VALUE",
    highlight: true,
    tagline: "The most comprehensive coaching experience. Full-year commitment for elite results.",
    cta: "Join 12 Month Elite",
    installmentId: "twelve_month_installment",
    installmentLabel: "or 2 payments of $1,750",
    perks: [
      "Full 12-month elite programming",
      "Continuous plan optimization",
      "All coaching support included",
      "Highest-tier accountability",
    ],
  },
  {
    id: "elite_vip",
    name: "Elite VIP",
    price: "$620",
    unit: "USD / month",
    tag: "VIP",
    tagline: "Everything in Monthly Coaching PLUS elevated, more personal support.",
    cta: "Apply for Elite VIP",
    perks: [
      "Everything in Monthly Coaching",
      "One private video call per month",
      "More personalized communication",
      "Higher level of individual support",
    ],
  },
];

const ProgramPerk = ({ text }) => (
  <li className="flex items-start gap-2 text-white/75 text-sm">
    <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={3} />
    <span>{text}</span>
  </li>
);

const ProgramCard = ({ program, index, loadingId, onCheckout }) => {
  const p = program;
  const perksList = (p.perks || []).map((perk) => (
    <ProgramPerk key={perk} text={perk} />
  ));
  const isHighlight = !!p.highlight;
  const cardClass = isHighlight
    ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/[0.08] to-black/60 shadow-[0_25px_60px_-20px_rgba(212,175,55,0.35)] lg:-translate-y-4"
    : "border-white/10 bg-black/40 hover:border-[#D4AF37]/40 hover:-translate-y-1";
  const tagClass = isHighlight
    ? "bg-[#D4AF37] text-black"
    : "bg-white/10 text-white border border-white/20";
  const priceClass = isHighlight ? "text-[#D4AF37]" : "text-white";
  const ctaBtnClass = isHighlight
    ? "bg-[#D4AF37] text-black hover:bg-white"
    : "bg-white text-black hover:bg-[#D4AF37]";
  const waMessage = `Hi Chris, I'd like to enroll in the ${p.name} program.`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className={`relative flex flex-col p-7 sm:p-8 border transition-all duration-300 ${cardClass}`}
      data-testid={`program-card-${p.id}`}
    >
      {p.tag ? (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] tracking-[0.3em] uppercase font-bold ${tagClass}`}
        >
          {isHighlight ? <Star className="w-3 h-3 inline mr-1 -mt-0.5" fill="currentColor" /> : null}
          {p.tag}
        </div>
      ) : null}

      {p.id === "elite_vip" ? <Crown className="w-5 h-5 text-[#D4AF37] mb-3" /> : null}

      <h3
        className="text-white text-xl sm:text-2xl uppercase font-bold tracking-wide mb-2"
        style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}
      >
        {p.name}
      </h3>

      <div className="mb-4">
        <span
          className={`font-display text-4xl sm:text-5xl leading-none ${priceClass}`}
          style={{ fontFamily: "'Bebas Neue', sans-serif" }}
        >
          {p.price}
        </span>
        <p className="text-white/50 text-xs tracking-wider uppercase mt-1">{p.unit}</p>
        {p.installmentLabel ? (
          <p className="text-[#D4AF37]/80 text-[11px] mt-1 italic">{p.installmentLabel}</p>
        ) : null}
      </div>

      <p className="text-white/70 text-sm leading-relaxed mb-5">{p.tagline}</p>

      <ul className="space-y-2.5 mb-7 flex-grow">{perksList}</ul>

      <div className="space-y-2 mt-auto">
        <button
          type="button"
          onClick={() => onCheckout(p.id)}
          disabled={loadingId === p.id}
          className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[11px] sm:text-xs tracking-[0.25em] uppercase font-bold transition-all duration-300 disabled:opacity-60 ${ctaBtnClass}`}
          data-testid={`program-cta-${p.id}`}
        >
          {loadingId === p.id ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {p.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </>
          )}
        </button>

        {p.installmentId ? (
          <button
            type="button"
            onClick={() => onCheckout(p.installmentId)}
            disabled={loadingId === p.installmentId}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60"
            data-testid={`program-cta-${p.installmentId}`}
          >
            {loadingId === p.installmentId ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <span>Pay 1st installment · $1,750</span>
            )}
          </button>
        ) : null}

        <a
          href={buildWhatsAppLink(waMessage)}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-white/70 hover:text-[#25D366] transition-colors"
          data-testid={`program-wa-${p.id}`}
        >
          <WhatsAppIcon size={14} />
          Enroll via WhatsApp
        </a>
      </div>
    </motion.div>
  );
};

const CoachingProgramsSection = () => {
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (programId) => {
    setLoadingId(programId);
    try {
      const { data } = await axios.post(`${API}/coaching/checkout`, {
        program_id: programId,
        origin_url: window.location.origin,
      });
      if (data && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Unable to start checkout. Please try again.");
      }
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed. Try again or WhatsApp us.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <section
      id="coaching-programs"
      className="relative py-24 sm:py-32 lg:py-40 bg-black overflow-hidden"
      data-testid="coaching-programs-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.05)_0%,transparent_50%)] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-14 sm:mb-20"
        >
          <p className="text-[#D4AF37] text-[11px] sm:text-xs tracking-[0.4em] uppercase font-bold mb-4">
            Choose Your Program
          </p>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.95] tracking-tight mb-6"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            COACHING <span className="text-[#D4AF37]">PROGRAMS</span>
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            One official pricing. Four coaching tiers. Built for athletes ready to commit.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {PROGRAMS.map((p, i) => (
            <ProgramCard
              key={p.id}
              program={p}
              index={i}
              loadingId={loadingId}
              onCheckout={startCheckout}
            />
          ))}
        </div>

        <p className="text-center text-white/40 text-[11px] tracking-widest uppercase mt-12">
          Secure payment powered by Stripe · Test mode
        </p>
      </div>
    </section>
  );
};

export default CoachingProgramsSection;
